import * as React from 'react';
import {
  ErrorResponse,
  IncomingInvoice,
  IncomingInvoiceTotal,
  PageResponseEnvelope,
  SetToBeTransferredRequest,
  SetTransferredRequest
} from '../api/dtos/dtos';
import {RouteComponentProps, withRouter} from 'react-router';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {Table} from 'antd';
import locales from '../locales';
import * as paths from '../paths';
import * as tableUtils from '../utils/table-utils';
import {
  filterableColumn,
  GridState, searchableColumn,
  searchableSortableColumn,
  sortableColumn
} from '../utils/table-utils';
import furoApi from '../api/FuroApi';
import * as routerUtils from '../utils/router-utils';
import {ColumnProps, TableRowSelection} from 'antd/lib/table';
import {formatDate, formatDateOrNull, toIsoDateString, toMomentOrThrow} from '../utils/date-utils';
import {alignRightNoWrap, groupNumber, withCurrency} from '../utils/number-utils';
import {
  getIncomingInvoiceStateFilters,
  IncomingInvoiceState,
  mapStateLabel,
  renderIncomingInvoiceStateDot
} from './incoming-invoice-state';
import {Link} from 'react-router-dom';
import {InvoiceDateRangePicker} from './InvoiceDateRangePicker';
import * as moment from 'moment';
import {isIncomingInvoiceOverdue, renderOverdueIcon} from './incoming-invoice-overdue';
import IncomingInvoiceActions from './IncomingInvoiceActions';
import {CheckboxProps} from 'antd/lib/checkbox';
import './IncomingInvoices.css';
import {getPaymentTypeFilters, getPaymentTypeLabel, PaymentType} from '../common/PaymentType';
import {downloadFile} from '../utils/file-utils';

interface State extends GridState<IncomingInvoice> {
  selectedItems: IncomingInvoice[];
  error?: ErrorResponse;
  start?: string;
  end?: string;
  totals?: IncomingInvoiceTotal;
}

const STATE_KEY: string = 'state';
const PAYMENT_TYPE_KEY: string = 'paymentType';
const defaultStartDate = moment().subtract(3, 'month');
const defaultEndDate = moment();

const initialGridState = (): GridState<IncomingInvoice> => {
  return {
    ...tableUtils.initialGridState(),
    filters: {
      [STATE_KEY]: [IncomingInvoiceState.OPEN, IncomingInvoiceState.TO_BE_TRANSFERRED, IncomingInvoiceState.TRANSFERRED],
      [PAYMENT_TYPE_KEY]: [PaymentType.CASH, PaymentType.TRANSFER],
    }
  }
};

class IncomingInvoicesPage extends React.Component<RouteComponentProps, State> {

  public state: State = {
    selectedItems: [],

    // Default initial grid state
    ...initialGridState(),

    // Initial sorting
    sortColumnKey: 'name',
    sortOrder: 'ascend',

    // Date range filter
    start: toIsoDateString(defaultStartDate),
    end: toIsoDateString(defaultEndDate),

    // Override with cached state from Router
    ...this.props.location.state,
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {totals, data, error, selectedItems, start, end} = this.state;
    const columns: Array<ColumnProps<IncomingInvoice>> = [
      {
        ...filterableColumn('', STATE_KEY, this.state, r => renderIncomingInvoiceStateDot(r.state), getIncomingInvoiceStateFilters(), true),
        className: 'align-center state-cell-width-fix'
      },
      searchableSortableColumn(locales.invoice_number, 'invoiceNumber', this.state,  this.renderInvoiceNumber, 130),
      searchableSortableColumn(locales.appellation, 'name', this.state, r => r.name, 150),
      searchableSortableColumn(locales.type, 'type', this.state, r => r.type),
      searchableSortableColumn(locales.company_name, 'companyName', this.state, r => r.companyName),
      filterableColumn(locales.paymentType, PAYMENT_TYPE_KEY, this.state, r => getPaymentTypeLabel(r.paymentType), getPaymentTypeFilters(), true),
      sortableColumn(locales.sum, 'sum', this.state, r => this.renderSumWithCurrency(r)),
      sortableColumn(locales.issue_date, 'issueDate', this.state, r => formatDate(r.issueDate)),
      // sortableColumn(locales.completion_date, 'completionDate', this.state, r => formatDateOrNull(r.completionDate)),
      sortableColumn(locales.payment_deadline, 'paymentDeadline', this.state, r => this.renderPaymentDeadline(r)),
      sortableColumn(locales.paid, 'paidDate', this.state, r => formatDateOrNull(r.paidDate)),
      searchableColumn(locales.responsible_user, 'responsibleUser', this.state, r => r.responsibleUser),

    ];
    // columns.forEach(col => {
    //   if (!col.width) {
    //     col.width = 130;
    //   }
    // });

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        <InvoiceDateRangePicker
          defaultStart={start ? toMomentOrThrow(start) : undefined}
          defaultEnd={end ? toMomentOrThrow(end) : undefined}
          onRangeChanged={this.onRangeChange}
        />
        <IncomingInvoiceActions
          showStateActionButtons={selectedItems.length > 0}
          onCreateInvoiceClicked={this.onAddButtonClicked}
          onMarkAsToBeTransferred={this.onMarkToBeTransferredClicked}
          onMarkAsTransferred={this.markAsTransferred}
          onExportToBeTransferredItem={this.exportToBeTransferredItems}
        />
        <Table
          dataSource={data}
          columns={columns}
          bordered={true}
          size="small"
          scroll={{x: 1154}}
          rowKey={tableUtils.idRowKeyFn}
          rowSelection={this.rowSelection()}
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
        />
        {totals && this.renderTotals(totals)}
        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderInvoiceNumber = (invoice: IncomingInvoice) => {
    const attachmentCount = invoice.attachments?.length ?? 0;
    return (
      <div className="incoming-invoice-name-container">
        <Link to={paths.viewIncomingInvoicePath(invoice.id)}>{invoice.invoiceNumber}</Link>
        {attachmentCount > 0 && <span className="attachments">{attachmentCount} {locales.attachment}</span>}
      </div>
    );
  };

  private renderPaymentDeadline = (invoice: IncomingInvoice) => {
    return (
      <span>
        {formatDate(invoice.paymentDeadline)}
        {isIncomingInvoiceOverdue(invoice) && <>&nbsp;&nbsp;{renderOverdueIcon()}</>}
      </span>
    );
  };

  private renderSumWithCurrency = (invoice: IncomingInvoice) => {
    const unit = invoice.currency === 'EUR' ? locales.currency_eur : locales.app_currency;
    return alignRightNoWrap(<>{groupNumber(invoice.sum)} {unit}</>);
  };

  private renderTotals = (totals: IncomingInvoiceTotal) => {
    const count = totals.totals.map(t => t.count).reduce((a: number, b: number) => a + b, 0);
    const sum = totals.totals.map(t => t.sum).reduce((a: number, b: number) => a + b, 0);
    return (
      <div className="incoming-invoice-totals">
        <table>
          <tbody>
          {totals.totals.map((total) =>
            <tr key={total.state}>
              <td><b>{total.count}</b> {mapStateLabel(total.state)} tétel</td>
              <td><b>{withCurrency(groupNumber(total.sum))}</b></td>
            </tr>
          )}
          <tr className="summary-separator">
            <td>Összesen <b>{count}</b> tétel</td>
            <td><b>{withCurrency(groupNumber(sum))}</b></td>
          </tr>
          </tbody>
        </table>
      </div>
    );
  };

  private rowSelection = (): TableRowSelection<IncomingInvoice> => {
    const selectedRowKeys = this.state.selectedItems.map((record) => tableUtils.idRowKeyFn(record));
    return {
      selectedRowKeys,
      onChange: (selectedKeys: string[], selectedItems: IncomingInvoice[]) => {
        this.setState({selectedItems}, () => {
          routerUtils.cacheComponentStateForLocation(this.props, this.state);
        });
      },
      getCheckboxProps: (invoice: IncomingInvoice): CheckboxProps => ({
        disabled: invoice.state !== IncomingInvoiceState.OPEN,
      }),
    };
  };

  private onAddButtonClicked = () => {
    this.props.history.push(paths.createIncomingInvoicePath);
  };

  private onRangeChange = (start: moment.Moment, end: moment.Moment) => {
    this.setState({
      start: start ? toIsoDateString(start) : undefined,
      end: end ? toIsoDateString(end) : undefined,
    }, () => this.reload());
  };

  private onGridChange = (newState: GridState<IncomingInvoice>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const gridRequest = this.createGridRequest();
    const filterRequest = {filters: gridRequest.filters};
    const promise = Promise
      .all([
        furoApi.incomingInvoices.getAll(gridRequest),
        furoApi.incomingInvoices.getTotals(filterRequest),
      ])
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private createGridRequest = () => {
    const {start, end} = this.state;
    const request = tableUtils.toGridRequest(this.state);
    if (start) {
      request.filters = {
        ...request.filters,
        'start': [start],
      };
    }
    if (end) {
      request.filters = {
        ...request.filters,
        'end': [end],
      };
    }
    return request;
  };

  private onReloadSuccess = (result: [PageResponseEnvelope<IncomingInvoice>, IncomingInvoiceTotal]) => {
    this.setState({
      ...tableUtils.toGridState(result[0]),
      totals: result[1],
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onMarkToBeTransferredClicked = () => {
    const request: SetToBeTransferredRequest = {
      incomingInvoiceIds: this.state.selectedItems.map((invoice) => invoice.id!!)
    };
    const promise = furoApi.incomingInvoices.setToBeTransferred(request)
      .then(this.clearSelection)
      .then(this.reload)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private markAsTransferred = (paidDate: string) => {
    const request: SetTransferredRequest = {
      paidDate
    };
    const promise = furoApi.incomingInvoices.setTransferred(request)
      .then(this.clearSelection)
      .then(this.reload)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private exportToBeTransferredItems = (name: string) => {
    const {start, end} = this.state;
    const promise = furoApi.incomingInvoices
      .exportToBeTransferredItems(name, start, end)
      .then(buffer => {
        downloadFile(new Blob([buffer], {type: 'application/vnd.ms-excel'}), `${name}.xls`);
        this.setState({error: undefined});
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});

  private clearSelection = () => {
    this.setState({selectedItems: []});
  };
}

export default withRouter(IncomingInvoicesPage);



// WEBPACK FOOTER //
// ./src/incoming-invoices/IncomingInvoicesPage.tsx